import { useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Star } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { api, errMsg } from "@/lib/api";
import { usePoll } from "@/hooks/usePoll";
import { useAuth } from "@/context/AuthContext";
import { fmtPrice, fmtPct, fmtCompact, fmtTime, pnlClass, CATEGORY_LABEL } from "@/lib/format";
import { PageHeader, DataBadge, AssetIcon, MarketStatus, Empty } from "@/components/kit";
import { Sparkline } from "@/components/Sparkline";
import { MarketHeatmap } from "@/components/MarketHeatmap";

const TABS = [["all", "Todos"], ["fav", "Favoritos"], ...Object.entries(CATEGORY_LABEL)];

const Row = ({ a, fav, onFav, spark, prev }) => {
  const navigate = useNavigate();
  const flash = prev == null || prev === a.price ? "" : a.price > prev ? "flash-up" : "flash-down";
  return (
    <tr key={`${a.symbol}-${a.price}`} className={`border-b border-border hover:bg-accent/40 transition-colors ${flash}`} data-testid={`market-row-${a.symbol}`}>
      <td className="py-3 pl-4 pr-2">
        <button aria-label="Favoritar" onClick={() => onFav(a.symbol)} data-testid={`fav-toggle-${a.symbol}`}>
          <Star className={`h-4 w-4 ${fav ? "fill-primary text-primary" : "text-muted-foreground"}`} />
        </button>
      </td>
      <td className="py-3 pr-4">
        <div className="flex items-center gap-3">
          <AssetIcon symbol={a.symbol} category={a.category} />
          <div><div className="font-semibold">{a.name}</div><div className="num text-xs text-muted-foreground">{a.symbol}</div></div>
        </div>
      </td>
      <td className="py-3 pr-4 text-right"><div className="num font-semibold" data-testid={`market-price-${a.symbol}`}>{fmtPrice(a.price, a.decimals)}</div>
        <div className="mt-1"><DataBadge type={a.data_type} testid={`market-badge-${a.symbol}`} /></div></td>
      <td className={`py-3 pr-4 text-right num ${pnlClass(a.change_pct)}`}><div>{fmtPct(a.change_pct)}</div><div className="text-xs">{a.change > 0 ? "+" : ""}{fmtPrice(a.change, a.decimals)}</div></td>
      <td className="py-3 pr-4 text-right num text-xs hidden lg:table-cell"><div className="down">{fmtPrice(a.bid, a.decimals)}</div><div className="up">{fmtPrice(a.ask, a.decimals)}</div></td>
      <td className="py-3 pr-4 text-right num text-xs hidden xl:table-cell"><div>{fmtPrice(a.high, a.decimals)}</div><div className="text-muted-foreground">{fmtPrice(a.low, a.decimals)}</div></td>
      <td className="py-3 pr-4 text-right num text-xs hidden xl:table-cell">{fmtCompact(a.volume)}</td>
      <td className="py-3 pr-4 hidden md:table-cell"><Sparkline data={spark} testid={`spark-${a.symbol}`} /></td>
      <td className="py-3 pr-4 hidden md:table-cell"><MarketStatus status={a.market_status} /><div className="num text-[11px] text-muted-foreground mt-1">{fmtTime(a.updated_at)}</div></td>
      <td className="py-3 pr-4 text-right">
        <Button size="sm" className="rounded-lg" disabled={!a.trading_enabled} onClick={() => navigate(`/negociar/${a.symbol}`)} data-testid={`trade-btn-${a.symbol}`}>Negociar</Button>
      </td>
    </tr>
  );
};

export default function Markets() {
  const { user, setUser } = useAuth();
  const [assets, setAssets] = useState([]);
  const [spark, setSpark] = useState({});
  const [tab, setTab] = useState("all");
  const [q, setQ] = useState("");
  const prev = useRef({});
  const [prevMap, setPrevMap] = useState({});
  const favs = user.favorites || [];
  const nav = useNavigate();
  const openTrade = (a) => (a.trading_enabled ? nav(`/negociar/${a.symbol}`) : toast.error("Negociação deste ativo está suspensa no momento"));

  usePoll(() => api.get("/market/assets").then((r) => {
    setPrevMap(prev.current);
    prev.current = Object.fromEntries(r.data.map((a) => [a.symbol, a.price]));
    setAssets(r.data);
  }).catch(() => {}), 3000);
  usePoll(() => api.get("/market/sparklines").then((r) => setSpark(r.data)).catch(() => {}), 300000);

  const rows = useMemo(() => assets.filter((a) =>
    (tab === "all" || (tab === "fav" ? favs.includes(a.symbol) : a.category === tab)) &&
    (!q || `${a.symbol} ${a.name}`.toLowerCase().includes(q.toLowerCase()))), [assets, tab, q, favs]);

  const toggleFav = async (s) => {
    const on = favs.includes(s);
    try {
      await (on ? api.delete(`/me/favorites/${s}`) : api.post(`/me/favorites/${s}`));
      setUser({ ...user, favorites: on ? favs.filter((x) => x !== s) : [...favs, s] });
      toast.success(on ? "Removido dos favoritos" : "Adicionado aos favoritos");
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="markets-page">
      <PageHeader eyebrow="Mercado financeiro" title="Mercados"
        desc="Cotações atualizadas automaticamente. Criptomoedas em tempo real pela Binance (AO VIVO); Forex, ações, índices e commodities são cotações reais do Google Finance (REAL), que podem ter atraso conforme a bolsa." />
      <MarketHeatmap onSelect={openTrade} hint="clique em um ativo para abrir o gráfico e a boleta de ordem" />
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between mb-5">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="h-auto flex-wrap">{TABS.map(([k, l]) => <TabsTrigger key={k} value={k} data-testid={`market-tab-${k}`} className="px-4 py-2">{l}</TabsTrigger>)}</TabsList>
        </Tabs>
        <div className="relative lg:w-80">
          <Search className="absolute left-3 top-3.5 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Pesquisar ativo ou símbolo" className="h-11 pl-9" value={q} onChange={(e) => setQ(e.target.value)} data-testid="market-search-input" />
        </div>
      </div>
      <div className="surface overflow-x-auto">
        <table className="w-full text-sm" data-testid="markets-table">
          <thead className="text-left eyebrow border-b border-border">
            <tr><th className="py-3 pl-4" /><th className="py-3">Ativo</th><th className="py-3 pr-4 text-right">Cotação</th><th className="py-3 pr-4 text-right">Variação</th>
              <th className="py-3 pr-4 text-right hidden lg:table-cell">Venda/Compra</th><th className="py-3 pr-4 text-right hidden xl:table-cell">Máx/Mín</th>
              <th className="py-3 pr-4 text-right hidden xl:table-cell">Volume</th><th className="py-3 pr-4 hidden md:table-cell">24h</th>
              <th className="py-3 pr-4 hidden md:table-cell">Mercado</th><th /></tr>
          </thead>
          <tbody>{rows.map((a) => <Row key={a.symbol} a={a} fav={favs.includes(a.symbol)} onFav={toggleFav} spark={spark[a.symbol]} prev={prevMap[a.symbol]} />)}</tbody>
        </table>
        {rows.length === 0 && <Empty text={assets.length ? "Nenhum ativo encontrado." : "Carregando cotações…"} testid="markets-empty" />}
      </div>
    </div>
  );
}
